/**
 * BM Versioning MVI Reducer
 *
 * Architecture: MVI Pattern - Reducer (Pure Function)
 * NO side effects, NO I/O, NO mutations.
 */

import type { IBmVersioningState } from "./state";
import type { TVersioningIntent } from "./intents";
import { createInitialState } from "./state";

/**
 * Pure reducer function for versioning state.
 * All state transitions are handled here.
 */
export function reducer(
  state: IBmVersioningState,
  intent: TVersioningIntent
): IBmVersioningState {
  switch (intent.type) {
    // ========================================================================
    // Feature Selection
    // ========================================================================

    case "SET_CURRENT_FEATURE":
      return {
        ...state,
        domain: {
          ...state.domain,
          currentFeatureId: intent.featureId,
          // Reset history when feature changes
          history: null,
          isDirty: false,
        },
        ui: {
          ...state.ui,
          error: null,
        },
      };

    // ========================================================================
    // Dirty State
    // ========================================================================

    case "SET_DIRTY":
      return {
        ...state,
        domain: {
          ...state.domain,
          isDirty: intent.isDirty,
        },
      };

    // ========================================================================
    // History Loading
    // ========================================================================

    case "LOAD_HISTORY_START":
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoadingHistory: true,
          error: null,
        },
      };

    case "LOAD_HISTORY_SUCCESS":
      return {
        ...state,
        domain: {
          ...state.domain,
          history: intent.history,
        },
        ui: {
          ...state.ui,
          isLoadingHistory: false,
        },
      };

    case "LOAD_HISTORY_ERROR":
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoadingHistory: false,
          error: intent.error,
        },
      };

    // ========================================================================
    // Commit
    // ========================================================================

    case "COMMIT_START":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCommitting: true,
          error: null,
          isCommitDialogOpen: false,
        },
      };

    case "COMMIT_SUCCESS":
      return {
        ...state,
        domain: {
          ...state.domain,
          // Update to new feature ID
          currentFeatureId: intent.newFeatureId,
          // Reset dirty state after successful commit
          isDirty: false,
          // Add new commit to history (prepend, as it's the new HEAD)
          history: state.domain.history
            ? {
                headFeatureId: intent.newFeatureId,
                commits: [
                  // New HEAD (the new feature)
                  {
                    ...intent.commitInfo,
                    featureId: intent.newFeatureId,
                    isHead: true,
                  },
                  // Previous commits (update the old HEAD to be non-head)
                  ...state.domain.history.commits.map((c) => ({
                    ...c,
                    isHead: false,
                  })),
                ],
              }
            : null,
        },
        ui: {
          ...state.ui,
          isCommitting: false,
        },
      };

    case "COMMIT_ERROR":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCommitting: false,
          error: intent.error,
        },
      };

    // ========================================================================
    // Checkout
    // ========================================================================

    case "CHECKOUT_START":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCheckingOut: true,
          error: null,
          checkoutTargetId: null,
        },
      };

    case "CHECKOUT_SUCCESS":
      // Note: After checkout, the parent component should switch to the new feature
      // and reload history. We just reset state here.
      return {
        ...state,
        domain: {
          ...state.domain,
          currentFeatureId: intent.newFeatureId,
          isDirty: false,
          history: null, // Will be reloaded by parent
        },
        ui: {
          ...state.ui,
          isCheckingOut: false,
          isHistoryPanelOpen: false,
        },
      };

    case "CHECKOUT_ERROR":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCheckingOut: false,
          error: intent.error,
        },
      };

    // ========================================================================
    // UI
    // ========================================================================

    case "TOGGLE_HISTORY_PANEL":
      return {
        ...state,
        ui: {
          ...state.ui,
          isHistoryPanelOpen: !state.ui.isHistoryPanelOpen,
        },
      };

    case "OPEN_COMMIT_DIALOG":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCommitDialogOpen: true,
        },
      };

    case "CLOSE_COMMIT_DIALOG":
      return {
        ...state,
        ui: {
          ...state.ui,
          isCommitDialogOpen: false,
        },
      };

    case "SET_CHECKOUT_TARGET":
      return {
        ...state,
        ui: {
          ...state.ui,
          checkoutTargetId: intent.targetFeatureId,
        },
      };

    case "CLEAR_ERROR":
      return {
        ...state,
        ui: {
          ...state.ui,
          error: null,
        },
      };

    default:
      return state;
  }
}

/**
 * Initial state for the reducer
 */
export const initialState: IBmVersioningState = createInitialState();
